import React, { useState } from 'react';

import { Box, Divider, Flex, FormControl, Input } from '@chakra-ui/react';
import { MenuItem } from '@material-ui/core';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import styled from 'styled-components';
import { StyledTextField } from 'styles/admin';
import { Divider1, FlexContainer } from 'styles/common';

import { ANSWER_TYPES, QUESTION_FORM_SOURCES, QUESTION_TYPES } from 'constants/ProjectConstants';
import {
  CommentAnswerIcon,
  NumberIcon,
  OptionsIcon,
  ScaleIcon,
  SmartColoredEmojiIcon,
  StarAnswerIcon,
  TextBoxIcon,
} from 'constants/icons';
import theme from 'constants/theme';

import { Typography4 } from 'uiToolkit/Typography';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import QuestionPreview from './QuestionPreview';
import EmojiAnswerType from './answerTypes/emojiAnswerType';
import NumberRatingAnswerType from './answerTypes/numberRatingAnswerType';
import OptionsAnswerType from './answerTypes/optionsAnswerType';
import ScaleAnswerType from './answerTypes/scaleAnswerType';
import StarRatingAnswerType from './answerTypes/starRatingAnswerType';
import { StyledSelect } from './answerTypes/styles';

export const MAX_CHARACTER_LIMIT_FOR_QUESTION = 1024;

const QuestionCharacterLimitErrorUi = ({ questionCharLimitError, questionCharacterCount }) => (
  <Flex className="character-count" mt="4px" justifyContent="space-between">
    {questionCharLimitError ? (
      <TextBodyMedium color="functional.C62C02" align="left">
        Character limit exceeded.
      </TextBodyMedium>
    ) : null}

    <TextBodyMedium ml="auto" color={questionCharLimitError ? 'functional.C62C02' : 'text.g3'}>
      {questionCharacterCount}/{MAX_CHARACTER_LIMIT_FOR_QUESTION}
    </TextBodyMedium>
  </Flex>
);

const AllVariantsQuestionText = ({ handleVariantChange, errors, question, isDuplicating }) => {
  const questionVariants = [
    { label: 'Self', value: 'SELF' },
    { label: 'Manager', value: 'MANAGER' },
    { label: 'Peer', value: 'PEER' },
    { label: 'Upward', value: 'UPWARD' },
    { label: 'Dotted Line Manager', value: 'DLM' },
  ];

  const getVariantDescription = (variant) => {
    if (variant === 'SELF') {
      return question?.description || '';
    }
    if (question?.providerToDescription) {
      return question?.providerToDescription?.[variant] || '';
    }
    return '';
  };

  return questionVariants.map(({ value: variant, label }) => (
    <StyledBox key={variant}>
      <FormControl isInvalid={errors[variant]}>
        <TextLabel sx={{ mb: 1 }}>{label}</TextLabel>
        <Input
          name={variant}
          onChange={(event) => handleVariantChange(event)}
          value={getVariantDescription(variant)}
          disabled={question.isEditable === false && !isDuplicating}
        />
        <QuestionCharacterLimitErrorUi
          questionCharLimitError={getVariantDescription(variant).length > MAX_CHARACTER_LIMIT_FOR_QUESTION}
          questionCharacterCount={getVariantDescription(variant).length}
        />
        {variant === 'SELF' && <TextLabel color="text.g3">This will be reflected above</TextLabel>}
        {getVariantDescription(variant).length > MAX_CHARACTER_LIMIT_FOR_QUESTION
          ? null
          : errors[variant] === 'SELF'
            ? question.description
            : errors[variant] && <TextLabel color="alerts.negative">This cannot be left empty</TextLabel>}
      </FormControl>
    </StyledBox>
  ));
};

const QuestionForm = ({ source, question, competencies, drivers, updateQuestion, setQuestion, isDuplicating }) => {
  const isVariantsChecked = question?.hasCustomDescription;
  const allVariantsQuestions = question?.providerToDescription ?? {};
  const [errors, setErrors] = useState({
    PEER: false,
    SELF: false,
    MANAGER: false,
    UPWARD: false,
    DLM: false,
  });
  const answerTypes = ANSWER_TYPES[source];
  const questionTypes = QUESTION_TYPES[source];
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  const description =
    (question?.hasCustomDescription ? question?.providerToDescription?.SELF : question.description) || '';
  const questionCharacterCount = question.description?.length;
  const questionCharLimitError = description.length > MAX_CHARACTER_LIMIT_FOR_QUESTION;

  const handleVariantChange = (event) => {
    const { value } = event.target;
    const { name } = event.target;
    if (value === '') {
      setErrors((prevState) => ({ ...prevState, [name]: true }));
    } else {
      setErrors((prevState) => ({ ...prevState, [name]: false }));
    }

    const tempObj = { ...allVariantsQuestions, [name]: value };
    handleChange('providerToDescription', tempObj);
    if (name === 'SELF') onDescriptionChange(value);
  };

  const handleChange = (param, value) => updateQuestion(param, value);

  const onDescriptionChange = (value) => {
    setQuestion((prevState) => ({ ...prevState, description: value }));
  };

  const handleVariantCheckbox = (event) => {
    handleChange('hasCustomDescription', event.target.checked);
    setQuestion((prevState) => ({
      ...prevState,
      providerToDescription: null,
    }));
    if (event.target.checked) {
      setQuestion((prevState) => ({
        ...prevState,
        providerToDescription: {
          ...prevState.providerToDescription,
          SELF: question.description,
          MANAGER: question.description,
          PEER: question.description,
          UPWARD: question.description,
          DLM: question.description,
        },
      }));
    }
  };

  const getLabelIcon = (answerType, questionType) => {
    switch (answerType) {
      case 'STAR_RATING':
        return <StarAnswerIcon size="fixed20px" rightGutter={1.5} />;
      case 'NUMBER_RATING':
        return questionType === 'ENGAGEMENT' ? (
          <NumberIcon size="fixed20px" rightGutter={1.5} />
        ) : (
          <ScaleIcon size="fixed20px" rightGutter={1.5} />
        );
      case 'DROP_DOWN':
        return <OptionsIcon size="fixed20px" rightGutter={1.5} />;
      case 'MULTI_SELECT':
        return <OptionsIcon size="fixed20px" rightGutter={1.5} />;
      case 'EMOJI':
        return <SmartColoredEmojiIcon size="fixed20px" rightGutter={1.5} />;
      case 'COMMENT':
        return <CommentAnswerIcon size="fixed20px" rightGutter={1.5} />;
      case 'NONE':
        return <TextBoxIcon size="fixed20px" rightGutter={1.5} />;
      default:
    }
  };

  const isDisabled =
    !isDuplicating &&
    (source === QUESTION_FORM_SOURCES.ENGAGEMENT || source === QUESTION_FORM_SOURCES.REVIEW) &&
    question?.isEditable === false;

  return (
    <>
      <Container>
        {source === 'review' ? (
          <>
            <StyledTextField1
              fullWidth
              autoFocus
              name="description"
              variant="outlined"
              value={question?.hasCustomDescription ? question?.providerToDescription?.SELF : question.description}
              onChange={(e) => {
                handleChange('description', e.target.value);
                if (isVariantsChecked) {
                  setQuestion((prevState) => ({
                    ...prevState,
                    providerToDescription: {
                      ...prevState.providerToDescription,
                      SELF: e.target.value,
                      MANAGER: e.target.value,
                      PEER: e.target.value,
                      UPWARD: e.target.value,
                      DLM: e.target.value,
                    },
                  }));
                }
              }}
              placeholder="Type your question here"
              background={theme.palette.background.lightGrey4}
              disabled={
                isVariantsChecked ||
                isDisabled ||
                (source === 'engagement' && question?.isEditable === false && !isDuplicating)
              }
            />

            <QuestionCharacterLimitErrorUi
              questionCharLimitError={questionCharLimitError}
              questionCharacterCount={questionCharacterCount}
            />
          </>
        ) : (
          <>
            <StyledTextField1
              fullWidth
              autoFocus
              name="description"
              variant="outlined"
              value={question.description}
              onChange={(e) => {
                handleChange('description', e.target.value);
              }}
              placeholder="Type your question here"
              background={theme.palette.background.lightGrey4}
              disabled={(source === 'engagement' && question?.isEditable === false && !isDuplicating) || isDisabled}
            />
            <QuestionCharacterLimitErrorUi
              questionCharLimitError={questionCharLimitError}
              questionCharacterCount={questionCharacterCount}
            />
          </>
        )}
      </Container>

      {source === 'review' && (
        <Box m="0 1.5rem">
          <Checkbox
            isChecked={isVariantsChecked}
            isDisabled={isDisabled}
            onChange={(event) => handleVariantCheckbox(event)}
          >
            Add Variant questions for each reviewer type
          </Checkbox>
          {isVariantsChecked && (
            <TextLabel color="text.g2" sx={{ marginLeft: '1.5rem' }}>
              Customized text entered here for each reviewer type will be visible to the respective reviewer type when
              this question is added in the template for the reviewers
            </TextLabel>
          )}
        </Box>
      )}
      {source === 'review' && question?.hasCustomDescription && (
        <Container style={{ paddingBottom: 0 }}>
          <AllVariantsQuestionText
            handleVariantChange={handleVariantChange}
            errors={errors}
            question={question}
            isDuplicating={isDuplicating}
          />
          <Divider />
        </Container>
      )}

      <PaddedContainer>
        {source !== QUESTION_FORM_SOURCES.ONE_ON_ONE && (
          <>
            <FlexContainer style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <FlexContainer style={{ alignItems: 'center' }}>
                <Typography4 customColor={theme.palette.text.dark1} topGutter={1}>
                  {source === 'engagement' ? 'Choose Driver *' : 'Question Type'}
                </Typography4>
              </FlexContainer>
              {source === 'review' && (
                <StyledSelect
                  value={question.tagId || question.questionType || ''}
                  displayEmpty
                  variant="outlined"
                  onChange={(e) => handleChange('questionType', e.target.value)}
                  width="45%"
                  disabled={isDisabled}
                >
                  {questionTypes.map((questionType) => (
                    <StyledMenuItem bordered={questionType.value === 'QNA'} primary value={questionType.value}>
                      {questionType.label === 'Goal' ? moduleName : questionType.label}
                    </StyledMenuItem>
                  ))}
                  {competencies.map((competency) => (
                    <StyledMenuItem secondary value={competency.id}>
                      {competency.displayName}
                    </StyledMenuItem>
                  ))}
                </StyledSelect>
              )}
              {source === 'engagement' && (
                <StyledSelect
                  width="45%"
                  value={question.tagId || ''}
                  displayEmpty
                  variant="outlined"
                  onChange={(e) => handleChange('tagId', e.target.value)}
                  disabled={(question?.isEditable === false && !isDuplicating) || isDisabled}
                >
                  <StyledMenuItem value="" disabled>
                    Select driver
                  </StyledMenuItem>
                  {drivers.map((driver) => (
                    <StyledMenuItem value={driver.id}>{driver.displayName}</StyledMenuItem>
                  ))}
                </StyledSelect>
              )}
            </FlexContainer>
            <Divider1 topGutter={3} bottomGutter={3} />
          </>
        )}

        <FlexContainer style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography4 customColor={theme.palette.text.dark1} topGutter={1}>
            Answer Type
          </Typography4>
          <StyledSelect
            value={question.answerType === 'MULTI_SELECT' ? 'DROP_DOWN' : question.answerType}
            displayEmpty
            key={question.answerType === 'MULTI_SELECT' ? 'DROP_DOWN' : question.answerType}
            variant="outlined"
            onChange={(e) => handleChange('answerType', e.target.value)}
            width="45%"
            disabled={(source === 'engagement' && question?.isEditable === false && !isDuplicating) || isDisabled}
          >
            {answerTypes.map((option) => (
              <StyledMenuItem primary value={option.value}>
                <FlexContainer style={{ alignItems: 'center' }}>
                  {getLabelIcon(option.value, question.questionType)}
                  <Typography4>{option.label}</Typography4>
                </FlexContainer>
              </StyledMenuItem>
            ))}
          </StyledSelect>
        </FlexContainer>

        {question.answerType === 'STAR_RATING' && (
          <StarRatingAnswerType
            source={source}
            question={question}
            updateQuestion={updateQuestion}
            isDuplicating={isDuplicating}
          />
        )}
        {['QNA', 'GOAL', 'COMPETENCY'].includes(question.questionType) && question.answerType === 'NUMBER_RATING' && (
          <ScaleAnswerType
            source={source}
            question={question}
            updateQuestion={updateQuestion}
            isDuplicating={isDuplicating}
          />
        )}
        {['DROP_DOWN', 'MULTI_SELECT'].includes(question.answerType) && (
          <OptionsAnswerType
            question={question}
            updateQuestion={updateQuestion}
            source={source}
            isDuplicating={isDuplicating}
          />
        )}

        {/* engagement emoji question */}
        {question.answerType === 'EMOJI' && question.questionType === 'ENGAGEMENT' && (
          <EmojiAnswerType
            question={question}
            updateQuestion={updateQuestion}
            source={source}
            isDuplicating={isDuplicating}
          />
        )}

        {/* 1:1 emoji question */}
        {/* 
          commenting out as per requirement 
            - hide emoji scale dropdown for 1:1
        */}
        {/* {question.answerType === 'EMOJI' && question.questionType === 'EMOTION' && (
          <OneOnOneEmoji question={question} updateQuestion={updateQuestion} />
        )} */}

        {question.questionType === 'ENGAGEMENT' && question.answerType === 'NUMBER_RATING' && (
          <NumberRatingAnswerType
            question={question}
            updateQuestion={updateQuestion}
            source={source}
            isDuplicating={isDuplicating}
          />
        )}
        {/* {question.answerType === 'NONE' && (
          <TextAnswerType question={question} updateQuestion={updateQuestion} />
        )} */}

        {/* START - Preview Component for the selected question type */}
        <QuestionPreview question={question} />
        {/* END - Preview Component for the selected question type */}
      </PaddedContainer>
    </>
  );
};

const Container = styled.div`
  padding: 1.5rem 0 1rem 0;
  margin: 0 1.5rem;
`;

const PaddedContainer = styled.div`
  padding: 1rem 0 6rem 0;
  margin: 0 1.5rem;
`;

const StyledTextField1 = styled(StyledTextField)`
  input {
    font-size: 1.25rem;
    padding: 12px 14px;
  }
  fieldset {
    border: 1px solid ${theme.palette.borders.lighterGrey};
  }
`;

const StyledMenuItem = styled(MenuItem)`
  padding-left: 24px;
  padding-right: 24px;
  &:hover {
    background: none;
    .MuiTouchRipple-root {
      background-color: rgba(0, 0, 0, 0.04);
    }
  }

  &.Mui-selected {
    background: none;
    &:hover {
      background: none;
    }
    .MuiTouchRipple-root {
      background-color: rgba(0, 0, 0, 0.08);
    }
  }

  ${({ hidden }) =>
    hidden &&
    `
      display: none;
  `};
  ${({ primary }) =>
    primary &&
    `
      padding-top: 16px;
      padding-bottom: 16px;
      // margin: 0.5rem 0;
      .MuiTouchRipple-root {
        margin: 6px 0;
      }
  `};
  ${({ secondary }) =>
    secondary &&
    `
      color: #777;
  `};
  ${({ bordered }) =>
    bordered &&
    `
      border-top: 1px solid #eee;
      border-bottom: 1px solid #eee;
  `};
`;

const StyledBox = styled.div`
  // padding: 1.5rem 0 1rem 0;
  margin: 0 0 1.5rem 1.5rem;
  :last-child: {
    margin: 0;
  }
`;

export default QuestionForm;
